/**
 * Point d'entrée.
 *
 * Un garde-fou volontaire : `ErrorBoundary`. Le dashboard doit rester
 * consultable quand l'API est éteinte, mais aussi quand une réponse
 * inattendue casse un rendu — un écran blanc en salle TROC ne dit rien à
 * personne, un message qui nomme la page fautive permet de continuer.
 */

import { Component, StrictMode } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import './styles.css';

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // La console reste la trace exploitable pour l'exploitant ; on n'envoie
    // rien nulle part depuis le back-office.
    console.error('Rendu interrompu', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div style={{ padding: 32 }}>
        <div className="state state-error" role="alert">
          <span className="state-title">Le tableau de bord a rencontré une erreur d'affichage</span>
          <span className="state-detail">{error.message}</span>
          <div>
            <button type="button" className="btn btn-sm" onClick={() => this.setState({ error: null })}>
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }
}

const container = document.getElementById('root');
if (!container) throw new Error("Élément #root introuvable dans index.html");

createRoot(container).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
);
